import { useState, useEffect, useRef } from "react";
import { forgotPassword, verifyOtp, resetPassword, getLockStatus } from "./services/masterDataService";
import "./Login.css";

const Step = { USERNAME: 1, OTP: 2, RESET: 3, SUCCESS: 4 };
const BRAND_GREEN = '#0aa375';
const BRAND_GREEN_LIGHT = '#e6f6f1';

function useLockTimer() {
  const [lockSecondsLeft, setLockSecondsLeft] = useState(() => {
    const lockedUntil = sessionStorage.getItem("lockUntil");
    if (!lockedUntil) return 0;
    const remaining = Math.floor((parseInt(lockedUntil) - Date.now()) / 1000);
    return remaining > 0 ? remaining : 0;
  });

  useEffect(() => {
    const savedUsername = sessionStorage.getItem("lockedUsername");
    if (!savedUsername) return;
    const fetchLockFromApi = async () => {
      try {
        const data = await getLockStatus(savedUsername);
        if (data.isLocked && data.lockedUntil) {
          const lockUntilMs = new Date(data.lockedUntil).getTime();
          const remaining = Math.floor((lockUntilMs - Date.now()) / 1000);
          if (remaining > 0) { sessionStorage.setItem("lockUntil", lockUntilMs.toString()); setLockSecondsLeft(remaining); }
          else clearLock();
        } else clearLock();
      } catch { }
    };
    fetchLockFromApi();
  }, []);

  useEffect(() => {
    if (lockSecondsLeft <= 0) { clearLock(); return; }
    const interval = setInterval(() => {
      const lockedUntil = sessionStorage.getItem("lockUntil");
      if (!lockedUntil) { setLockSecondsLeft(0); return; }
      const remaining = Math.floor((parseInt(lockedUntil) - Date.now()) / 1000);
      if (remaining <= 0) { clearLock(); clearInterval(interval); }
      else setLockSecondsLeft(remaining);
    }, 1000);
    return () => clearInterval(interval);
  }, [lockSecondsLeft <= 0]);

  const clearLock = () => {
    sessionStorage.removeItem("lockUntil");
    sessionStorage.removeItem("lockedUsername");
    setLockSecondsLeft(0);
  };

  const applyLock = async (username, fallbackMinutes = 5) => {
    sessionStorage.setItem("lockedUsername", username);
    try {
      const data = await getLockStatus(username);
      if (data.isLocked && data.lockedUntil) {
        const lockUntilMs = new Date(data.lockedUntil).getTime();
        const remaining = Math.floor((lockUntilMs - Date.now()) / 1000);
        if (remaining > 0) { sessionStorage.setItem("lockUntil", lockUntilMs.toString()); setLockSecondsLeft(remaining); return; }
      }
    } catch { }
    const existingLock = sessionStorage.getItem("lockUntil");
    if (!existingLock || parseInt(existingLock) <= Date.now()) {
      const lockDurationMs = fallbackMinutes * 60 * 1000;
      const lockUntil = Date.now() + lockDurationMs;
      sessionStorage.setItem("lockUntil", lockUntil.toString());
      setLockSecondsLeft(Math.floor(lockDurationMs / 1000));
    } else {
      const remaining = Math.floor((parseInt(existingLock) - Date.now()) / 1000);
      setLockSecondsLeft(remaining > 0 ? remaining : 0);
    }
  };

  const formatLockTime = (totalSeconds) => {
    const minutes = Math.ceil(totalSeconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  return { lockSecondsLeft, applyLock, clearLock, formatLockTime };
}

function Alert({ type, message }) {
  if (!message) return null;
  const map = {
    error: { bg: "#fff5f5", border: "#feb2b2", text: "#c53030", icon: "⚠️" },
    success: { bg: "#f0fff4", border: "#9ae6b4", text: "#22543d", icon: "✓" },
    info: { bg: "#ebf8ff", border: "#90cdf4", text: "#2b6cb0", icon: "ℹ️" },
  };
  const s = map[type] || map.info;
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.border}`, color: s.text,
      borderRadius: "10px", padding: "11px 14px", fontSize: "13.5px",
      marginBottom: "18px", display: "flex", alignItems: "center", gap: "8px",
      fontWeight: 500,
    }}>
      <span>{s.icon}</span><span>{message}</span>
    </div>
  );
}

function ProgressSteps({ current }) {
  const steps = ["Verify User", "Verify OTP", "New Password"];
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "28px" }}>
      {steps.map((label, i) => {
        const num = i + 1;
        const done = current > num;
        const active = current === num;
        return (
          <div key={num} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : "none" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
              <div style={{
                width: 30, height: 30, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "12px", fontWeight: 700,
                background: done ? BRAND_GREEN : active ? "#fff" : "#f7fdf9",
                border: `2px solid ${done || active ? BRAND_GREEN : "#d4ead9"}`,
                color: done ? "#fff" : active ? BRAND_GREEN : "#8aab97",
                boxShadow: active ? `0 0 0 3px ${BRAND_GREEN_LIGHT}` : "none",
                transition: "all 0.3s ease",
              }}>
                {done ? "✓" : num}
              </div>
              <span style={{
                fontSize: "11px", fontWeight: active ? 700 : 500,
                color: active ? "#0d2416" : "#8aab97",
                whiteSpace: "nowrap", letterSpacing: "0.2px",
              }}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 1, height: "2px", margin: "0 10px", marginBottom: "20px",
                background: done ? BRAND_GREEN : "#d4ead9",
                transition: "background 0.3s ease",
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function Card({ children }) {
  return (
    <div style={{
      padding: "36px 40px",
      width: "100%",
      maxWidth: "440px",
      animation: "fadeCard 0.5s cubic-bezier(0.22,1,0.36,1) both",
    }}>
      {children}
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <label style={{
      display: "block", marginBottom: "7px",
      fontSize: "12px", fontWeight: 700,
      color: "#2d4a39", letterSpacing: "0.4px",
      textTransform: "uppercase",
      fontFamily: "'DM Sans', sans-serif",
    }}>{children}</label>
  );
}

function Input(props) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      onFocus={e => { setFocused(true); props.onFocus?.(e); }}
      onBlur={e => { setFocused(false); props.onBlur?.(e); }}
      style={{
        width: "100%", boxSizing: "border-box",
        padding: "0 14px", height: "44px",
        borderRadius: "10px",
        border: `1.5px solid ${focused ? BRAND_GREEN : "#d4ead9"}`,
        background: "#fff",
        color: "#0d2416",
        fontSize: "14px",
        fontFamily: "'DM Sans', sans-serif",
        outline: "none",
        transition: "all 0.2s ease",
        boxShadow: focused ? `0 0 0 3px rgba(10,163,117,0.1)` : "none",
        ...props.style,
      }}
    />
  );
}

function PrimaryButton({ children, loading, disabled, onClick }) {
  const [hovered, setHovered] = useState(false);
  const isDisabled = disabled || loading;
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: "100%", height: "46px",
        borderRadius: "10px", border: "none",
        cursor: isDisabled ? "not-allowed" : "pointer",
        fontSize: "14.5px", fontWeight: 600,
        fontFamily: "'DM Sans', sans-serif",
        letterSpacing: "0.2px",
        background: isDisabled ? "#c8dfd2" : hovered ? "#098f66" : BRAND_GREEN,
        color: isDisabled ? "#8aab97" : "#fff",
        transform: hovered && !isDisabled ? "translateY(-1px)" : "none",
        boxShadow: hovered && !isDisabled
          ? "0 6px 20px rgba(13,158,110,0.35)"
          : isDisabled ? "none" : "0 4px 14px rgba(13,158,110,0.25)",
        transition: "all 0.2s ease",
      }}
    >
      {loading ? (
        <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
          <span style={{
            width: 15, height: 15, borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.35)",
            borderTopColor: "#fff",
            display: "inline-block",
            animation: "spin 0.7s linear infinite",
          }} />
          Processing...
        </span>
      ) : children}
    </button>
  );
}

function UsernameStep({ onNext }) {
  const { lockSecondsLeft, applyLock, formatLockTime } = useLockTimer();
  const [username, setUsername] = useState(() => sessionStorage.getItem("lockedUsername") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!username.trim()) { setError("Please enter your username."); return; }
    setLoading(true); setError("");
    try {
      const data = await forgotPassword(username.trim());
      if (data.success) {
        sessionStorage.removeItem("lockUntil"); sessionStorage.removeItem("lockedUsername");
        onNext(username.trim());
      } else {
        if (data.isLocked) await applyLock(username.trim(), data.lockMinutes || 5);
        else setError(data.message || "Failed to send OTP.");
      }
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <ProgressSteps current={1} />
      <h2 style={S.title}>Forgot Password</h2>
      <p style={S.subtitle}>Enter your username and we'll send a 6-digit verification code to your registered email.</p>
      {lockSecondsLeft > 0
        ? <Alert type="error" message={`Account locked. Try again in ${formatLockTime(lockSecondsLeft)}.`} />
        : <Alert type="error" message={error} />}
      <div style={{ marginBottom: "22px" }}>
        <FieldLabel>Username</FieldLabel>
        <Input
          type="text" placeholder="e.g. john_doe"
          value={username} onChange={e => setUsername(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSubmit()}
          disabled={lockSecondsLeft > 0} autoFocus
        />
      </div>
      <PrimaryButton loading={loading} disabled={lockSecondsLeft > 0} onClick={handleSubmit}>
        Send Verification Code →
      </PrimaryButton>
    </Card>
  );
}

function OtpStep({ username, onNext, onBack }) {
  const { lockSecondsLeft, applyLock, formatLockTime } = useLockTimer();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(60);
  const [resending, setResending] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(null);
  const refs = useRef([]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(r => r - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp]; next[i] = val; setOtp(next);
    if (val && i < 5) refs.current[i + 1]?.focus();
  };
  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) refs.current[i - 1]?.focus();
  };
  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (text.length === 6) { setOtp(text.split("")); refs.current[5]?.focus(); }
    e.preventDefault();
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length < 6) { setError("Please enter all 6 digits."); return; }
    setLoading(true); setError("");
    try {
      const data = await verifyOtp(username, code);
      if (data.success) onNext(data.resetToken);
      else {
        if (data.isLocked) await applyLock(username, data.lockMinutes || 5);
        else { setError(data.message || "Invalid OTP."); setOtp(["", "", "", "", "", ""]); refs.current[0]?.focus(); }
      }
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true); setError("");
    try {
      const data = await forgotPassword(username);
      if (data.success) { setResendTimer(60); setOtp(["", "", "", "", "", ""]); refs.current[0]?.focus(); }
      else {
        if (data.isLocked) await applyLock(username, data.lockMinutes || 5);
        else setError(data.message || "Failed to resend code.");
      }
    } catch { setError("Network error. Please try again."); }
    finally { setResending(false); }
  };

  return (
    <Card>
      <ProgressSteps current={2} />
      <h2 style={S.title}>Security Verification</h2>
      <p style={S.subtitle}>
        A 6-digit code was sent to the email for <strong style={{ color: "#0d2416" }}>{username}</strong>.
      </p>
      {lockSecondsLeft > 0
        ? <Alert type="error" message={`Account locked. Try again in ${formatLockTime(lockSecondsLeft)}.`} />
        : <Alert type="error" message={error} />}

      <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginBottom: "26px" }} onPaste={handlePaste}>
        {otp.map((digit, i) => (
          <input
            key={i} ref={el => refs.current[i] = el}
            type="text" inputMode="numeric" maxLength={1} value={digit}
            onFocus={() => setFocusedIdx(i)} onBlur={() => setFocusedIdx(null)}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
            disabled={lockSecondsLeft > 0}
            style={{
              width: "46px", height: "54px", textAlign: "center",
              fontSize: "22px", fontWeight: 700,
              borderRadius: "10px",
              border: `2px solid ${focusedIdx === i ? BRAND_GREEN : digit ? "#2d4a39" : "#d4ead9"}`,
              background: "#fff", color: "#0d2416", outline: "none",
              transition: "all 0.2s ease",
              boxShadow: focusedIdx === i ? `0 0 0 3px rgba(10,163,117,0.1)` : "none",
              opacity: lockSecondsLeft > 0 ? 0.5 : 1,
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
        ))}
      </div>

      <PrimaryButton loading={loading} disabled={lockSecondsLeft > 0} onClick={handleVerify}>
        Verify & Continue →
      </PrimaryButton>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "18px", fontSize: "13px" }}>
        <button onClick={onBack} style={S.linkBtn}>← Back</button>
        {lockSecondsLeft > 0 ? (
          <span style={{ color: "#c53030", fontWeight: 600 }}>🔒 {formatLockTime(lockSecondsLeft)} remaining</span>
        ) : resendTimer > 0 ? (
          <span style={{ color: "#8aab97" }}>Resend in {resendTimer}s</span>
        ) : (
          <button onClick={handleResend} disabled={resending} style={{ ...S.linkBtn, color: BRAND_GREEN, fontWeight: 600 }}>
            {resending ? "Sending..." : "Resend Code"}
          </button>
        )}
      </div>
    </Card>
  );
}

function ResetStep({ username, resetToken, onSuccess }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const rules = [
    { label: "At least 8 characters long", pass: password.length >= 8 },
    { label: "Contains an uppercase letter", pass: /[A-Z]/.test(password) },
    { label: "Contains a number", pass: /\d/.test(password) },
    { label: "Passwords match exactly", pass: !!password && password === confirm },
  ];
  const allPass = rules.every(r => r.pass);

  const handleReset = async () => {
    if (!allPass) { setError("Please meet all password requirements."); return; }
    setLoading(true); setError("");
    try {
      const data = await resetPassword(username, password);
      if (data.success) onSuccess();
      else setError(data.message || "Failed to update credentials.");
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <Card>
      <ProgressSteps current={3} />
      <h2 style={S.title}>Set New Password</h2>
      <Alert type="error" message={error} />

      <div style={{ marginBottom: "16px" }}>
        <FieldLabel>New Password</FieldLabel>
        <div style={{ position: "relative" }}>
          <Input
            type={showPwd ? "text" : "password"}
            value={password} onChange={e => setPassword(e.target.value)}
            placeholder="Minimum 8 characters"
            style={{ paddingRight: "44px" }}
          />
          <button type="button" onClick={() => setShowPwd(v => !v)} style={{
            position: "absolute", right: "13px", top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer", padding: 0,
            fontSize: "15px", color: showPwd ? BRAND_GREEN : "#8aab97",
          }}>
          </button>
        </div>
      </div>

      <div style={{ marginBottom: "16px" }}>
        <FieldLabel>Confirm New Password</FieldLabel>
        <Input
          type={showPwd ? "text" : "password"}
          value={confirm} onChange={e => setConfirm(e.target.value)}
          placeholder="Repeat password"
          onKeyDown={e => e.key === "Enter" && handleReset()}
        />
      </div>
      <div style={{
        marginBottom: "22px", padding: "14px 16px",
        background: "#f7fdf9", borderRadius: "10px",
        border: "1px solid #d4ead9",
      }}>
        {rules.map((r, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: "8px",
            fontSize: "13px",
            color: r.pass ? "#0aa375" : "#8aab97",
            marginBottom: i < rules.length - 1 ? "7px" : 0,
            transition: "color 0.2s ease",
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <span style={{ fontSize: "12px", fontWeight: 700 }}>{r.pass ? "✓" : "○"}</span>
            {r.label}
          </div>
        ))}
      </div>

      <PrimaryButton loading={loading} disabled={!allPass} onClick={handleReset}>
        Save New Password →
      </PrimaryButton>
    </Card>
  );
}

function SuccessStep({ onLogin }) {
  return (
    <Card>
      <div style={{ textAlign: "center", padding: "12px 0 4px" }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%",
          background: BRAND_GREEN_LIGHT,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 20px",
          fontSize: "26px", color: BRAND_GREEN, fontWeight: "bold",
          boxShadow: `0 0 0 6px rgba(10,163,117,0.08)`,
        }}>✓</div>
        <h2 style={{ ...S.title, textAlign: "center", marginBottom: "10px" }}>Password Updated!</h2>
        <p style={{ ...S.subtitle, textAlign: "center", marginBottom: "28px" }}>
          Your credentials have been updated. You can now sign in with your new password.
        </p>
        <PrimaryButton onClick={onLogin}>Back to Login →</PrimaryButton>
      </div>
    </Card>
  );
}

function ForgotPassword({ onNavigateToLogin }) {
  const [step, setStep] = useState(Step.USERNAME);
  const [username, setUsername] = useState("");
  const [resetToken, setResetToken] = useState("");

  return (
    <div className="login-root">

      {/* LEFT PANEL — identical to Login */}
      <div className="login-left">
        <div className="left-content">
          <h1 className="left-brand">GREEN<span>BILLS</span></h1>
          <h6 className="title">Admin Panel</h6>
          <div className="left-features">
            <div className="feature-item">
              <div className="feature-icon">🛡️</div>
              <div>
                <div className="feature-title">Robust Security</div>
                <div className="feature-desc">Advanced protection, permissions, and logging for secure internal operations</div>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🔒</div>
              <div>
                <div className="feature-title">Secure by Design</div>
                <div className="feature-desc">Role-based access and enterprise-grade protection</div>
              </div>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📊</div>
              <div>
                <div className="feature-title">Smart Analytics</div>
                <div className="feature-desc">Real-time insights at your fingertips</div>
              </div>
            </div>
          </div>
          <div className="deco-circle c1"></div>
          <div className="deco-circle c2"></div>
          <div className="deco-circle c3"></div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="login-right" style={{ overflowY: "auto", position: "relative" }}>
        <button
          onClick={onNavigateToLogin}
          style={{
            position: "absolute",
            top: "20px",
            left: "20px",
            background: "none",
            border: "#d4ead9",
            borderRadius: "8px",
            padding: "7px 14px",
            fontSize: "20px",
            fontWeight: 500,
            color: "#6b8f7a",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontFamily: "'DM Sans', sans-serif",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = "#0aa375";
            e.currentTarget.style.color = "#0aa375";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = "#d4ead9";
            e.currentTarget.style.color = "#6b8f7a";
          }}
        >
          ←
        </button>

        {step === Step.USERNAME && (
          <UsernameStep onNext={un => { setUsername(un); setStep(Step.OTP); }} />
        )}
        {step === Step.OTP && (
          <OtpStep
            username={username}
            onNext={token => { setResetToken(token); setStep(Step.RESET); }}
            onBack={() => setStep(Step.USERNAME)}
          />
        )}
        {step === Step.RESET && (
          <ResetStep username={username} resetToken={resetToken} onSuccess={() => setStep(Step.SUCCESS)} />
        )}
        {step === Step.SUCCESS && (
          <SuccessStep onLogin={onNavigateToLogin || (() => setStep(Step.USERNAME))} />
        )}
      </div>

    </div>
  );
}

const S = {
  title: {
    fontSize: "22px", fontWeight: 700,
    color: "#0d2416", margin: "0 0 7px",
    letterSpacing: "-0.4px",
  },
  subtitle: {
    fontSize: "13.5px", color: "#6b8f7a",
    margin: "0 0 22px", lineHeight: "1.6",
    fontFamily: "'DM Sans', sans-serif",
  },
  linkBtn: {
    background: "none", border: "none", cursor: "pointer",
    color: "#6b8f7a", padding: 0, fontSize: "13px",
    fontWeight: 500, fontFamily: "'DM Sans', sans-serif",
  },
};

export default ForgotPassword;